import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useBooking } from '../../contexts/BookingContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import Icon from '../../components/AppIcon';
import NotificationBell from '../../components/ui/NotificationBell';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRBAC } from '../../contexts/RBACContext';
import { downloadReservationConfirmationPDF } from '../../utils/reservationPdfGenerator';

const ReservationRequest = () => {
  const { user, signOut } = useAuth();
  const { notifyAdmins, sendNotification } = useNotifications();
  const { addApprovedReservation: bookingAddApprovedReservation } = useBooking();
  const realtime = useRealtime();
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, isAdmin, isViewer, can } = useRBAC();
  const isAdminOrViewer = isAdmin || isViewer;

  const [highlightedId, setHighlightedId] = useState(null);
  const highlightRef = useRef(null);

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [filterGuest, setFilterGuest] = useState('');

  // Admin detail modal
  const [selectedReq, setSelectedReq] = useState(null);
  const [adminObservation, setAdminObservation] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [emailTracking, setEmailTracking] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(null);

  const [form, setForm] = useState({
    full_name: user?.user_metadata?.full_name || '',
    email: user?.email || '',
    phone: '',
    national_id: '',
    gender: '',
    nationality: '',
    department: '',
    requested_check_in: '',
    requested_check_out: '',
    expected_arrival_time: '',
    expected_departure_time: '',
    department_coordinator_email: '',
    preferred_building: '',
    financial_status: 'not_covered_by_senghor',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);

  const [allRequests, setAllRequests] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location?.search);
    const highlight = params?.get('highlight');
    if (highlight) {
      setHighlightedId(highlight);
      const newUrl = window.location?.pathname;
      window.history?.replaceState({}, '', newUrl);
    }
  }, [location?.search]);

  useEffect(() => {
    if (highlightedId && highlightRef?.current) {
      setTimeout(() => {
        highlightRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
      const timer = setTimeout(() => setHighlightedId(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [highlightedId, allRequests, myRequests]);

  useEffect(() => {
    if (user) {
      fetchMyRequests();
      if (isAdminOrViewer) fetchAllRequests();
    }
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      ?.channel('reservation_requests_realtime')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'reservation_requests' }, () => {
        fetchMyRequests();
        if (isAdminOrViewer) fetchAllRequests();
      })
      ?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [user?.id, isAdminOrViewer]);

  const fetchMyRequests = async () => {
    setRequestsLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        ?.from('reservation_requests')
        ?.select('*')
        ?.eq('guest_id', user?.id)
        ?.order('created_at', { ascending: false });
      if (!fetchError) setMyRequests(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setRequestsLoading(false);
    }
  };

  const fetchAllRequests = async () => {
    setAllLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        ?.from('reservation_requests')
        ?.select('*')
        ?.order('created_at', { ascending: false });
      if (!fetchError) setAllRequests(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setAllLoading(false);
    }
  };

  const fetchEmailTracking = async (reservationId) => {
    setTrackingLoading(true);
    try {
      const { data } = await supabase
        ?.from('email_tracking')
        ?.select('*')
        ?.eq('reservation_id', reservationId)
        ?.order('sent_at', { ascending: false });
      setEmailTracking(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data: inserted, error: insertError } = await supabase
        ?.from('reservation_requests')
        ?.insert({ ...form, guest_id: user?.id, status: 'pending' })
        ?.select()
        ?.single();
      if (insertError) throw insertError;

      await notifyAdmins({
        title: 'New Reservation Request',
        message: `${form?.full_name || user?.email} submitted a reservation request for ${form?.requested_check_in} → ${form?.requested_check_out}.`,
        type: 'info',
        relatedId: inserted?.id,
        relatedTable: 'reservation_requests',
      });

      setSuccess(true);
      fetchMyRequests();
    } catch (err) {
      setError(err?.message || 'Failed to submit request.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (req, status, observation = '') => {
    setApprovingId(req?.id);
    try {
      const updateData = {
        status,
        reviewed_by: user?.id,
        reviewed_at: new Date()?.toISOString(),
      };
      if (status === 'approved') {
        updateData.approved_at = new Date()?.toISOString();
        if (observation) updateData.admin_observation = observation;
      }

      const { error: updateError } = await supabase
        ?.from('reservation_requests')
        ?.update(updateData)
        ?.eq('id', req?.id);
      if (updateError) throw updateError;

      // Fetch updated record with reference_number
      const { data: updatedReq } = await supabase
        ?.from('reservation_requests')
        ?.select('*')
        ?.eq('id', req?.id)
        ?.single();

      const finalReq = updatedReq || { ...req, ...updateData };

      if (status === 'approved') {
        let newStay = null;
        if (realtime?.addApprovedReservation) {
          const { data } = await realtime?.addApprovedReservation(req);
          newStay = data;
        }
        if (!newStay) {
          newStay = bookingAddApprovedReservation(req);
        }

        if (req?.email) {
          try {
            await supabase?.functions?.invoke('send-reservation-email', {
              body: {
                guestEmail: req?.email,
                guestName: req?.full_name || 'Guest',
                residentId: finalReq?.reference_number || `REQ-${req?.id?.slice(0, 8) || ''}`,
                referenceNumber: finalReq?.reference_number || `REQ-${req?.id?.slice(0, 8) || ''}`,
                room: newStay?.room_number || newStay?.room || req?.preferred_building || '',
                building: req?.preferred_building || '',
                checkIn: req?.requested_check_in || '',
                checkOut: req?.requested_check_out || '',
                arrivalTime: req?.expected_arrival_time || '',
                departureTime: req?.expected_departure_time || '',
                financialStatus: req?.financial_status || '',
                nationality: req?.nationality || '',
                gender: req?.gender || '',
                nationalId: req?.national_id || '',
                department: req?.department || '',
                adminObservation: observation || req?.admin_observation || '',
                accommodationType: req?.accommodation_type || 'short-term',
                notes: req?.message || '',
                departmentCoordinatorEmail: req?.department_coordinator_email || '',
                language: language || 'en',
                reservationId: req?.id,
              },
            });
          } catch (emailErr) {
            console.warn('Reservation email failed (non-blocking):', emailErr?.message);
          }
        }
      }

      if (req?.guest_id) {
        const isApproved = status === 'approved';
        await sendNotification({
          userId: req?.guest_id,
          title: isApproved ? 'Reservation Approved ✓' : 'Reservation Rejected',
          message: isApproved
            ? `Your reservation request for ${req?.requested_check_in} → ${req?.requested_check_out} has been approved.`
            : `Your reservation request for ${req?.requested_check_in} → ${req?.requested_check_out} has been rejected.`,
          type: isApproved ? 'success' : 'error',
          relatedId: req?.id,
          relatedTable: 'reservation_requests',
        });
      }

      fetchAllRequests();
      fetchMyRequests();
      setShowDetailModal(false);
      setSelectedReq(null);
      setAdminObservation('');
    } catch (err) {
      setError(err?.message);
    } finally {
      setApprovingId(null);
    }
  };

  const handleOpenDetail = (req) => {
    setSelectedReq(req);
    setAdminObservation(req?.admin_observation || '');
    setShowDetailModal(true);
    if (isAdmin) fetchEmailTracking(req?.id);
  };

  const handleDownloadPDF = async (req) => {
    setPdfDownloading(req?.id);
    try {
      await downloadReservationConfirmationPDF(req, language);
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setPdfDownloading(null);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const filteredRequests = allRequests?.filter((req) => {
    if (filterStatus !== 'all' && req?.status !== filterStatus) return false;
    if (filterDate && req?.created_at && !req?.created_at?.startsWith(filterDate)) return false;
    if (filterGuest && !req?.full_name?.toLowerCase()?.includes(filterGuest?.toLowerCase())) return false;
    return true;
  });

  const inputClass = "w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors";

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };

  const statusLabels = {
    pending: language === 'fr' ? 'En attente' : 'Pending',
    approved: language === 'fr' ? 'Approuvée' : 'Approved',
    rejected: language === 'fr' ? 'Rejetée' : 'Rejected',
  };

  const filterStatusOptions = [
    { value: 'all', label: language === 'fr' ? 'Tous' : 'All' },
    { value: 'pending', label: language === 'fr' ? 'En attente' : 'Pending' },
    { value: 'approved', label: language === 'fr' ? 'Approuvées' : 'Approved' },
    { value: 'rejected', label: language === 'fr' ? 'Rejetées' : 'Rejected' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="Home" size={18} color="#FFFFFF" />
          </div>
          <div>
            <span className="font-bold text-gray-900 text-sm">US Réservation Résidences</span>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                role === 'admin' ? 'bg-red-100 text-red-700' :
                role === 'viewer' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
              }`}>
                {role?.charAt(0)?.toUpperCase() + role?.slice(1)}
              </span>
              <span className="text-xs text-gray-500">{user?.email}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg text-gray-600 hover:text-gray-900 hover:border-gray-400 transition-all"
          >
            <Icon name="Globe" size={13} />
            {language === 'en' ? 'FR' : 'EN'}
          </button>
          <NotificationBell />
          {isAdminOrViewer && (
            <button
              onClick={() => navigate('/occupancy-analytics')}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Icon name="LayoutDashboard" size={14} />
              {language === 'fr' ? 'Tableau de bord' : 'Dashboard'}
            </button>
          )}
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Icon name="LogOut" size={14} />
            {language === 'fr' ? 'Déconnexion' : 'Sign Out'}
          </button>
        </div>
      </header>
      <div className="max-w-5xl mx-auto p-6 space-y-8">

        {/* Admin/Viewer: All Requests Panel */}
        {isAdminOrViewer && (
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-gray-900">
                  {language === 'fr' ? 'Toutes les demandes de réservation' : 'All Reservation Requests'}
                </h2>
                <span className="text-xs text-gray-500">
                  {filteredRequests?.length} / {allRequests?.length} {language === 'fr' ? 'demandes' : 'requests'}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg p-1">
                  {filterStatusOptions?.map((opt) => (
                    <button
                      key={opt?.value}
                      onClick={() => setFilterStatus(opt?.value)}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        filterStatus === opt?.value
                          ? 'bg-white shadow-sm text-gray-900 border border-gray-200'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {opt?.label}
                      {opt?.value !== 'all' && (
                        <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                          opt?.value === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          opt?.value === 'approved'? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {allRequests?.filter(r => r?.status === opt?.value)?.length}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-1.5 bg-white">
                  <Icon name="Calendar" size={13} className="text-gray-400" />
                  <input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e?.target?.value)}
                    className="text-xs text-gray-700 outline-none bg-transparent"
                  />
                  {filterDate && (
                    <button onClick={() => setFilterDate('')} className="text-gray-400 hover:text-gray-600">
                      <Icon name="X" size={12} />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-1.5 bg-white flex-1 min-w-[160px]">
                  <Icon name="Search" size={13} className="text-gray-400" />
                  <input
                    type="text"
                    value={filterGuest}
                    onChange={(e) => setFilterGuest(e?.target?.value)}
                    placeholder={language === 'fr' ? 'Rechercher par nom...' : 'Search by guest name...'}
                    className="text-xs text-gray-700 outline-none bg-transparent w-full"
                  />
                  {filterGuest && (
                    <button onClick={() => setFilterGuest('')} className="text-gray-400 hover:text-gray-600">
                      <Icon name="X" size={12} />
                    </button>
                  )}
                </div>

                {(filterStatus !== 'all' || filterDate || filterGuest) && (
                  <button
                    onClick={() => { setFilterStatus('all'); setFilterDate(''); setFilterGuest(''); }}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Icon name="FilterX" size={12} />
                    {language === 'fr' ? 'Réinitialiser' : 'Clear'}
                  </button>
                )}
              </div>
            </div>

            {allLoading ? (
              <div className="flex items-center justify-center py-10">
                <Icon name="Loader2" size={20} className="animate-spin text-primary" />
              </div>
            ) : filteredRequests?.length === 0 ? (
              <div className="text-center py-10 text-gray-500 text-sm">
                {language === 'fr' ? 'Aucune demande trouvée' : 'No requests found'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{language === 'fr' ? 'Invité' : 'Guest'}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{language === 'fr' ? 'Dates & Horaires' : 'Dates & Times'}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{language === 'fr' ? 'Financier' : 'Financial'}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{language === 'fr' ? 'Soumis le' : 'Submitted'}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{language === 'fr' ? 'Statut' : 'Status'}</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{language === 'fr' ? 'Actions' : 'Actions'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredRequests?.map((req) => {
                      const isHighlighted = highlightedId === req?.id;
                      return (
                        <tr
                          key={req?.id}
                          ref={isHighlighted ? highlightRef : null}
                          className={`transition-all duration-500 ${
                            isHighlighted ? 'bg-amber-50 ring-2 ring-amber-400 ring-inset' : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {isHighlighted && (
                                <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0 animate-pulse" />
                              )}
                              <div>
                                <div className="font-medium text-gray-900">{req?.full_name}</div>
                                <div className="text-xs text-gray-500">{req?.email}</div>
                                <div className="text-xs text-gray-400">{req?.department}</div>
                                {req?.reference_number && (
                                  <div className="text-xs font-mono text-primary mt-0.5">{req?.reference_number}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">
                            <div className="font-medium">{req?.requested_check_in}</div>
                            <div>{req?.requested_check_out}</div>
                            {req?.expected_arrival_time && (
                              <div className="text-gray-400 flex items-center gap-1 mt-0.5">
                                <Icon name="Clock" size={10} />
                                {req?.expected_arrival_time}
                              </div>
                            )}
                            {req?.expected_departure_time && (
                              <div className="text-gray-400 flex items-center gap-1">
                                <Icon name="Clock" size={10} />
                                {req?.expected_departure_time}
                              </div>
                            )}
                            {req?.preferred_building && <div className="text-gray-400 mt-0.5">{req?.preferred_building}</div>}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              req?.financial_status === 'covered_by_senghor' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                            }`}>
                              {req?.financial_status === 'covered_by_senghor'
                                ? (language === 'fr' ? 'Pris en charge' : 'Covered')
                                : (language === 'fr' ? 'Non pris en charge' : 'Not Covered')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {req?.created_at ? new Date(req?.created_at)?.toLocaleDateString() : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors?.[req?.status] || 'bg-gray-100 text-gray-700'}`}>
                              {statusLabels?.[req?.status] || req?.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => handleOpenDetail(req)}
                                className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors"
                              >
                                <Icon name="Eye" size={12} />
                                {language === 'fr' ? 'Détails' : 'Details'}
                              </button>
                              {req?.status === 'approved' && (
                                <button
                                  onClick={() => handleDownloadPDF(req)}
                                  disabled={pdfDownloading === req?.id}
                                  className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors disabled:opacity-60"
                                >
                                  {pdfDownloading === req?.id
                                    ? <Icon name="Loader2" size={12} className="animate-spin" />
                                    : <Icon name="Download" size={12} />}
                                  PDF
                                </button>
                              )}
                              {role === 'admin' && req?.status === 'pending' && (
                                approvingId === req?.id ? (
                                  <Icon name="Loader2" size={16} className="animate-spin text-gray-400" />
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleUpdateStatus(req, 'approved')}
                                      className="flex items-center gap-1 px-2.5 py-1 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition-colors"
                                    >
                                      <Icon name="Check" size={12} />
                                      {language === 'fr' ? 'Approuver' : 'Approve'}
                                    </button>
                                    <button
                                      onClick={() => handleUpdateStatus(req, 'rejected')}
                                      className="flex items-center gap-1 px-2.5 py-1 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors"
                                    >
                                      <Icon name="X" size={12} />
                                      {language === 'fr' ? 'Rejeter' : 'Reject'}
                                    </button>
                                  </>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Guest: Submit Request Form */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <h2 className="font-bold text-gray-900 text-lg">
              {isAdminOrViewer
                ? (language === 'fr' ? 'Soumettre une demande de réservation' : 'Submit a Reservation Request')
                : (language === 'fr' ? 'Demander une réservation de chambre' : 'Request a Room Reservation')}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {language === 'fr' ? 'Remplissez vos informations et nous examinerons votre demande' : 'Fill in your details and we will review your request'}
            </p>
          </div>

          {success ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} className="text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{language === 'fr' ? 'Demande soumise !' : 'Request Submitted!'}</h3>
              <p className="text-gray-500 text-sm mb-6">
                {language === 'fr' ?'Votre demande de réservation a été soumise et est en attente d\'examen. Vous recevrez une notification lorsqu\'elle sera approuvée ou rejetée.'
                  : 'Your reservation request has been submitted and is pending review. You will receive an in-app notification when it is approved or rejected.'}
              </p>
              <button onClick={() => setSuccess(false)} className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors">
                {language === 'fr' ? 'Soumettre une autre demande' : 'Submit Another Request'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} className="flex-shrink-0" />
                  {error}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{language === 'fr' ? 'Nom complet *' : 'Full Name *'}</label>
                  <input required type="text" value={form?.full_name} onChange={(e) => setForm({ ...form, full_name: e?.target?.value })} placeholder={language === 'fr' ? 'Votre nom complet' : 'Your full name'} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{language === 'fr' ? 'E-mail *' : 'Email *'}</label>
                  <input required type="email" value={form?.email} onChange={(e) => setForm({ ...form, email: e?.target?.value })} placeholder="email@example.com" className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{language === 'fr' ? 'Téléphone' : 'Phone'}</label>
                  <input type="text" value={form?.phone} onChange={(e) => setForm({ ...form, phone: e?.target?.value })} placeholder="+216 XX XXX XXX" className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{language === 'fr' ? 'Passeport / ID national' : 'Passport / National ID'}</label>
                  <input type="text" value={form?.national_id} onChange={(e) => setForm({ ...form, national_id: e?.target?.value })} placeholder={language === 'fr' ? 'Numéro de passeport ou ID' : 'Passport or ID number'} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{language === 'fr' ? 'Genre' : 'Gender'}</label>
                  <select value={form?.gender} onChange={(e) => setForm({ ...form, gender: e?.target?.value })} className={inputClass}>
                    <option value="">{language === 'fr' ? 'Sélectionner le genre' : 'Select gender'}</option>
                    <option value="male">{language === 'fr' ? 'Homme' : 'Male'}</option>
                    <option value="female">{language === 'fr' ? 'Femme' : 'Female'}</option>
                    <option value="other">{language === 'fr' ? 'Autre' : 'Other'}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{language === 'fr' ? 'Nationalité' : 'Nationality'}</label>
                  <input type="text" value={form?.nationality} onChange={(e) => setForm({ ...form, nationality: e?.target?.value })} placeholder={language === 'fr' ? 'Votre nationalité' : 'Your nationality'} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{language === 'fr' ? 'Département / Faculté' : 'Department / Faculty'}</label>
                  <input type="text" value={form?.department} onChange={(e) => setForm({ ...form, department: e?.target?.value })} placeholder={language === 'fr' ? 'ex. Informatique' : 'e.g. Computer Science'} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{language === 'fr' ? 'Date d\'arrivée *' : 'Check-in Date *'}</label>
                  <input required type="date" value={form?.requested_check_in} onChange={(e) => setForm({ ...form, requested_check_in: e?.target?.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{language === 'fr' ? 'Date de départ *' : 'Check-out Date *'}</label>
                  <input required type="date" value={form?.requested_check_out} onChange={(e) => setForm({ ...form, requested_check_out: e?.target?.value })} className={inputClass} />
                </div>

                {/* NEW: Arrival & Departure Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <span className="flex items-center gap-1.5">
                      <Icon name="Clock" size={14} className="text-gray-400" />
                      {language === 'fr' ? 'Heure d\'arrivée prévue' : 'Expected Arrival Time'}
                    </span>
                  </label>
                  <input type="time" value={form?.expected_arrival_time} onChange={(e) => setForm({ ...form, expected_arrival_time: e?.target?.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <span className="flex items-center gap-1.5">
                      <Icon name="Clock" size={14} className="text-gray-400" />
                      {language === 'fr' ? 'Heure de départ prévue' : 'Expected Departure Time'}
                    </span>
                  </label>
                  <input type="time" value={form?.expected_departure_time} onChange={(e) => setForm({ ...form, expected_departure_time: e?.target?.value })} className={inputClass} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{language === 'fr' ? 'Bâtiment préféré' : 'Preferred Building'}</label>
                  <input type="text" value={form?.preferred_building} onChange={(e) => setForm({ ...form, preferred_building: e?.target?.value })} placeholder={language === 'fr' ? 'ex. F1, G2, H...' : 'e.g. F1, G2, H...'} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{language === 'fr' ? 'Statut financier *' : 'Financial Status *'}</label>
                  <select required value={form?.financial_status} onChange={(e) => setForm({ ...form, financial_status: e?.target?.value })} className={inputClass}>
                    <option value="covered_by_senghor">{language === 'fr' ? 'Pris en charge par Senghor' : 'Covered by Senghor'}</option>
                    <option value="not_covered_by_senghor">{language === 'fr' ? 'Non pris en charge par Senghor' : 'Not Covered by Senghor'}</option>
                  </select>
                </div>

                {/* NEW: Department Coordinator Email */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <span className="flex items-center gap-1.5">
                      <Icon name="UserCheck" size={14} className="text-gray-400" />
                      {language === 'fr' ? 'Email du coordinateur de département' : 'Department Coordinator Email'}
                    </span>
                  </label>
                  <input
                    type="email"
                    value={form?.department_coordinator_email}
                    onChange={(e) => setForm({ ...form, department_coordinator_email: e?.target?.value })}
                    placeholder={language === 'fr' ? 'coordinateur@universite.org (optionnel)' : 'coordinator@university.org (optional)'}
                    className={inputClass}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {language === 'fr' ?'Le coordinateur recevra une copie de la confirmation lors de l\'approbation.' :'The coordinator will receive a copy of the confirmation upon approval.'}
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{language === 'fr' ? 'Message / Notes supplémentaires' : 'Message / Additional Notes'}</label>
                  <textarea value={form?.message} onChange={(e) => setForm({ ...form, message: e?.target?.value })} placeholder={language === 'fr' ? 'Toute information supplémentaire sur votre séjour...' : 'Any additional information about your stay...'} rows={3} className={inputClass} />
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full py-3 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {loading
                  ? <><Icon name="Loader2" size={16} className="animate-spin" /> {language === 'fr' ? 'Envoi en cours...' : 'Submitting...'}</>
                  : <><Icon name="Send" size={16} /> {language === 'fr' ? 'Soumettre la demande de réservation' : 'Submit Reservation Request'}</>}
              </button>
            </form>
          )}
        </div>

        {/* My Requests History */}
        {myRequests?.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-900">{language === 'fr' ? 'Mes demandes' : 'My Requests'}</h3>
              <span className="text-xs text-gray-500">{myRequests?.length} {language === 'fr' ? 'total' : 'total'}</span>
            </div>
            <div className="divide-y divide-gray-100">
              {myRequests?.map((req) => {
                const isHighlighted = highlightedId === req?.id;
                return (
                  <div
                    key={req?.id}
                    ref={isHighlighted ? highlightRef : null}
                    className={`px-6 py-4 flex items-center justify-between transition-all duration-500 ${
                      isHighlighted ? 'bg-amber-50 ring-2 ring-amber-400 ring-inset' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {isHighlighted && <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{req?.requested_check_in} → {req?.requested_check_out}</div>
                        {(req?.expected_arrival_time || req?.expected_departure_time) && (
                          <div className="text-xs text-gray-400 flex items-center gap-2 mt-0.5">
                            {req?.expected_arrival_time && (
                              <span className="flex items-center gap-1">
                                <Icon name="Clock" size={10} />
                                {language === 'fr' ? 'Arrivée' : 'Arrival'}: {req?.expected_arrival_time}
                              </span>
                            )}
                            {req?.expected_departure_time && (
                              <span className="flex items-center gap-1">
                                <Icon name="Clock" size={10} />
                                {language === 'fr' ? 'Départ' : 'Departure'}: {req?.expected_departure_time}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-0.5">
                          {req?.preferred_building || (language === 'fr' ? 'Pas de préférence' : 'No preference')} · {req?.department || (language === 'fr' ? 'Pas de département' : 'No department')}
                        </div>
                        {req?.reference_number && (
                          <div className="text-xs font-mono text-primary mt-0.5">{req?.reference_number}</div>
                        )}
                        <div className="text-xs text-gray-400 mt-0.5">
                          {language === 'fr' ? 'Soumis le' : 'Submitted'}: {req?.created_at ? new Date(req?.created_at)?.toLocaleDateString() : '—'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors?.[req?.status] || 'bg-gray-100 text-gray-700'}`}>
                        {statusLabels?.[req?.status] || req?.status}
                      </span>
                      {req?.status === 'approved' && (
                        <button
                          onClick={() => handleDownloadPDF(req)}
                          disabled={pdfDownloading === req?.id}
                          className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors disabled:opacity-60"
                        >
                          {pdfDownloading === req?.id
                            ? <Icon name="Loader2" size={12} className="animate-spin" />
                            : <Icon name="FileDown" size={12} />}
                          {language === 'fr' ? 'Télécharger PDF' : 'Download PDF'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {/* Detail Modal */}
      {showDetailModal && selectedReq && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h3 className="font-bold text-gray-900">
                  {language === 'fr' ? 'Détails de la demande' : 'Reservation Request Details'}
                </h3>
                {selectedReq?.reference_number && (
                  <span className="text-xs font-mono text-primary">{selectedReq?.reference_number}</span>
                )}
              </div>
              <button onClick={() => { setShowDetailModal(false); setSelectedReq(null); setEmailTracking([]); }} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Guest Info */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  {language === 'fr' ? 'Informations sur l\'invité' : 'Guest Information'}
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    [language === 'fr' ? 'Nom complet' : 'Full Name', selectedReq?.full_name],
                    [language === 'fr' ? 'Email' : 'Email', selectedReq?.email],
                    [language === 'fr' ? 'Téléphone' : 'Phone', selectedReq?.phone],
                    [language === 'fr' ? 'Nationalité' : 'Nationality', selectedReq?.nationality],
                    [language === 'fr' ? 'Genre' : 'Gender', selectedReq?.gender],
                    [language === 'fr' ? 'Passeport / ID' : 'Passport / ID', selectedReq?.national_id],
                    [language === 'fr' ? 'Département' : 'Department', selectedReq?.department],
                  ]?.map(([label, value]) => value ? (
                    <div key={label}>
                      <div className="text-xs text-gray-400">{label}</div>
                      <div className="font-medium text-gray-900">{value}</div>
                    </div>
                  ) : null)}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  {language === 'fr' ? 'Détails de la réservation' : 'Reservation Details'}
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    [language === 'fr' ? 'Bâtiment' : 'Building', selectedReq?.preferred_building],
                    [language === 'fr' ? 'Date d\'arrivée' : 'Check-in', selectedReq?.requested_check_in],
                    [language === 'fr' ? 'Date de départ' : 'Check-out', selectedReq?.requested_check_out],
                    [language === 'fr' ? 'Heure d\'arrivée prévue' : 'Expected Arrival Time', selectedReq?.expected_arrival_time],
                    [language === 'fr' ? 'Heure de départ prévue' : 'Expected Departure Time', selectedReq?.expected_departure_time],
                    [language === 'fr' ? 'Coordinateur' : 'Coordinator Email', selectedReq?.department_coordinator_email],
                  ]?.map(([label, value]) => value ? (
                    <div key={label}>
                      <div className="text-xs text-gray-400">{label}</div>
                      <div className="font-medium text-gray-900">{value}</div>
                    </div>
                  ) : null)}
                  <div>
                    <div className="text-xs text-gray-400">{language === 'fr' ? 'Statut financier' : 'Financial Status'}</div>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedReq?.financial_status === 'covered_by_senghor' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {selectedReq?.financial_status === 'covered_by_senghor'
                        ? (language === 'fr' ? 'Pris en charge' : 'Covered')
                        : (language === 'fr' ? 'Non pris en charge' : 'Not Covered')}
                    </span>
                  </div>
                </div>
                {selectedReq?.message && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-400 mb-1">{language === 'fr' ? 'Message' : 'Message'}</div>
                    <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selectedReq?.message}</div>
                  </div>
                )}
              </div>

              {/* Admin Observation */}
              {role === 'admin' && (
                <div className="border-t border-gray-100 pt-4">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {language === 'fr' ? 'Observation de l\'administrateur' : 'Admin Observation'}
                  </label>
                  <textarea
                    value={adminObservation}
                    onChange={(e) => setAdminObservation(e?.target?.value)}
                    placeholder={language === 'fr' ? 'Ajouter une observation...' : 'Add an observation...'}
                    rows={3}
                    className={inputClass}
                  />
                </div>
              )}
              {role !== 'admin' && selectedReq?.admin_observation && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {language === 'fr' ? 'Observation de l\'administrateur' : 'Admin Observation'}
                  </div>
                  <div className="text-sm text-gray-700 bg-blue-50 rounded-lg p-3">{selectedReq?.admin_observation}</div>
                </div>
              )}

              {/* Admin Actions */}
              {role === 'admin' && selectedReq?.status === 'pending' && (
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => handleUpdateStatus(selectedReq, 'approved', adminObservation)}
                    disabled={approvingId === selectedReq?.id}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-60"
                  >
                    {approvingId === selectedReq?.id ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Check" size={16} />}
                    {language === 'fr' ? 'Approuver' : 'Approve'}
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(selectedReq, 'rejected', adminObservation)}
                    disabled={approvingId === selectedReq?.id}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-60"
                  >
                    <Icon name="X" size={16} />
                    {language === 'fr' ? 'Rejeter' : 'Reject'}
                  </button>
                </div>
              )}

              {/* PDF Download */}
              {selectedReq?.status === 'approved' && (
                <div className="border-t border-gray-100 pt-4">
                  <button
                    onClick={() => handleDownloadPDF(selectedReq)}
                    disabled={pdfDownloading === selectedReq?.id}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60"
                  >
                    {pdfDownloading === selectedReq?.id
                      ? <><Icon name="Loader2" size={16} className="animate-spin" /> {language === 'fr' ? 'Génération...' : 'Generating...'}</>
                      : <><Icon name="FileDown" size={16} /> {language === 'fr' ? 'Télécharger la confirmation PDF' : 'Download PDF Confirmation'}</>}
                  </button>
                </div>
              )}

              {/* Email Tracking (Admin only) */}
              {isAdmin && (
                <div className="border-t border-gray-100 pt-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Icon name="Mail" size={13} />
                    {language === 'fr' ? 'Suivi des emails' : 'Email Tracking'}
                  </h4>
                  {trackingLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Icon name="Loader2" size={14} className="animate-spin" />
                      {language === 'fr' ? 'Chargement...' : 'Loading...'}
                    </div>
                  ) : emailTracking?.length === 0 ? (
                    <div className="text-sm text-gray-400 italic">
                      {language === 'fr' ? 'Aucun email envoyé' : 'No emails sent yet'}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {emailTracking?.map((track) => (
                        <div key={track?.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg text-xs">
                          <div>
                            <div className="font-medium text-gray-900">{track?.recipient_email}</div>
                            <div className="text-gray-500 mt-0.5">
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold mr-1.5 ${
                                track?.recipient_type === 'guest' ? 'bg-blue-100 text-blue-700' :
                                track?.recipient_type === 'coordinator'? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {track?.recipient_type === 'guest' ? (language === 'fr' ? 'Invité' : 'Guest') :
                                 track?.recipient_type === 'coordinator' ? (language === 'fr' ? 'Coordinateur' : 'Coordinator') :
                                 'CC'}
                              </span>
                              {track?.sent_at ? new Date(track?.sent_at)?.toLocaleString() : '—'}
                            </div>
                            {track?.error_message && (
                              <div className="text-red-500 mt-0.5">{track?.error_message}</div>
                            )}
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            track?.status === 'sent' ? 'bg-green-100 text-green-700' :
                            track?.status === 'delivered'? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {track?.status === 'sent' ? (language === 'fr' ? 'Envoyé' : 'Sent') :
                             track?.status === 'delivered' ? (language === 'fr' ? 'Livré' : 'Delivered') :
                             (language === 'fr' ? 'Échec' : 'Failed')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationRequest;
